import type { DeepPartial } from '@apollo/client/utilities'
import type {
  Angkatan,
  Corps,
  Jabatan,
  Maybe,
  Pangkat,
  Scalars,
  UnitKerja
} from '~/apollo/gql/graphql'
import type { CorpsOption, Option, UnitKerjaOption } from '~/types/form'

type Personel = {
  angkatan?: Maybe<Angkatan>
  corps?: Maybe<Corps>
  created_at: Scalars['Timestamp']['output']
  deleted_at?: Maybe<Scalars['Timestamp']['output']>
  email?: Maybe<Scalars['String']['output']>
  gender: Scalars['String']['output']
  id: Scalars['Float']['output']
  is_superadmin: Scalars['Boolean']['output']
  is_user_simpeg: Scalars['Boolean']['output']
  jabatan?: Maybe<Jabatan>
  jabatan_custom?: Maybe<Scalars['String']['output']>
  jabatan_kepala?: Maybe<UnitKerja>
  jabatan_type: Scalars['String']['output']
  lms_role: Scalars['String']['output']
  name: Scalars['String']['output']
  nrp: Scalars['String']['output']
  ojs_api_key?: Maybe<Scalars['String']['output']>
  pangkat?: Maybe<Pangkat>
  phone?: Maybe<Scalars['String']['output']>
  photo: Scalars['String']['output']
  status: Scalars['String']['output']
  unit_kerja?: Maybe<UnitKerja>
  unit_organisasi?: Maybe<UnitKerja>
  unit_kerja_custom?: Maybe<Scalars['String']['output']>
  unit_organisasi_custom?: Maybe<Scalars['String']['output']>
  satker?: Maybe<UnitKerja>
  sub_satker?: Maybe<UnitKerja>
}

type PersonelData = {
  nrp?: string
  name?: string
  gender?: Option<string>
  angkatan?: Option<number>
  pangkat?: Option<number>
  corps?: CorpsOption
  unit_kerja?: UnitKerjaOption
  unit_organisasi?: Option<string>
  jabatan?: Option<string>
  satker?: UnitKerjaOption
  sub_satker?: UnitKerjaOption
  unit_kerja_type?: EUnitKerjaType
  unit_organisasi_type?: EUnitKerjaType
  jabatan_type?: EJabatanType
  unit_kerja_custom?: string
  unit_organisasi_custom?: string
  jabatan_custom?: string
}

export function mapPersonelData(
  sourceData: DeepPartial<Personel>,
  targetData: Partial<PersonelData> = {}
): PersonelData {
  const result = { ...targetData } as PersonelData

  // Basic info
  if (sourceData.name) result.name = sourceData.name
  if (sourceData.nrp) result.nrp = sourceData.nrp

  // Gender
  if (sourceData.gender !== undefined) {
    result.gender = {
      label: sourceData.gender === EGender.MALE ? 'Laki-laki' : 'Perempuan',
      value: sourceData.gender
    }
  }

  // Angkatan
  if (sourceData.angkatan) {
    result.angkatan = {
      label: sourceData.angkatan.name,
      value: sourceData.angkatan.id
    }
  }

  // Pangkat
  if (sourceData.pangkat) {
    const angkatanId = result.angkatan?.value
    result.pangkat = {
      label: getPangkatLabel(sourceData.pangkat, angkatanId),
      value: sourceData.pangkat.id
    }
  }

  // Corps
  if (sourceData.corps) {
    result.corps = {
      label: sourceData.corps.name,
      value: sourceData.corps.id,
      abbr: sourceData.corps.abbr,
      abbr_fem: sourceData.corps.abbr_fem
    }
  }

  // Unit kerja
  if (sourceData.unit_kerja) {
    result.unit_kerja = {
      label: sourceData.unit_kerja.hirarki,
      value: sourceData.unit_kerja.kode
    }
    result.unit_kerja_type = EUnitKerjaType.SIMPEG
  } else if (sourceData.unit_kerja_custom) {
    result.unit_kerja_custom = sourceData.unit_kerja_custom
    result.unit_kerja_type = EUnitKerjaType.CUSTOM
  }

  // Unit Organisasi
  if (sourceData.unit_organisasi) {
    result.unit_organisasi = {
      label: sourceData.unit_organisasi.name,
      value: sourceData.unit_organisasi.kode
    }
  } else if (sourceData.unit_organisasi_custom) {
    result.unit_organisasi_custom = sourceData.unit_organisasi_custom
    result.unit_organisasi_type = EUnitKerjaType.CUSTOM
  }

  // Satker
  if (sourceData.satker) {
    result.satker = {
      label: sourceData.satker.hirarki,
      value: sourceData.satker.kode
    }
  }

  // Sub Satker
  if (sourceData.sub_satker) {
    result.sub_satker = {
      label: sourceData.sub_satker.hirarki,
      value: sourceData.sub_satker.kode
    }
  }

  // Jabatan with type handling
  if (sourceData.jabatan_type !== undefined) {
    result.jabatan_type = sourceData.jabatan_type as EJabatanType

    if (sourceData.jabatan_type === EJabatanType.KEPALA && sourceData.jabatan_kepala) {
      result.jabatan = {
        label: sourceData.jabatan_kepala.jabatan_name,
        value: sourceData.jabatan_kepala.kode
      }
    } else if (sourceData.jabatan_type === EJabatanType.FUNGSIONAL && sourceData.jabatan) {
      result.jabatan = {
        label: sourceData.jabatan.name,
        value: sourceData.jabatan.kode
      }
    } else if (sourceData.jabatan_custom) {
      result.jabatan_custom = sourceData.jabatan_custom
    }
  }

  return result
}

/**
 * Gets the appropriate label for a pangkat based on angkatan id
 */
export function getPangkatLabel(pangkat: Partial<Pangkat>, angkatanId?: number): string {
  if (angkatanId === 1) {
    return pangkat.name_ad || pangkat.name
  } else if (angkatanId === 2) {
    return pangkat.name_al || pangkat.name
  } else if (angkatanId === 3) {
    return pangkat.name_au || pangkat.name
  }
  return pangkat.name
}

/**
 * Checks if an angkatan id indicates TNI membership
 */
export function isAngkatanTni(angkatanId?: number): angkatanId is 1 | 2 | 3 {
  return angkatanId === 1 || angkatanId === 2 || angkatanId === 3
}

/**
 * Formats a full name with rank and corps
 */
export function formatFullName(data: PersonelData): string {
  const pangkat = data.pangkat?.value ? data.pangkat.label : null

  const corps = data.corps?.value
    ? data.gender?.value === EGender.MALE
      ? data.corps.abbr
      : data.corps.abbr_fem
    : null

  const isTNI = isAngkatanTni(data.angkatan?.value)

  const isBintang = isTNI && [91, 92, 93, 94].includes(data.pangkat?.value ?? -1)

  const first = isTNI && !isBintang ? [pangkat, corps].filter(Boolean).join(' ') : pangkat

  return [first, data.name].filter(Boolean).join(' ')
}

/**
 * Formats a full position with unit and position
 */
export function formatFullPosition(data: PersonelData): string {
  const unitKerja = data.unit_kerja?.value ? data.unit_kerja?.label : null
  const jabatan = data.jabatan?.value ? data.jabatan?.label : null
  const unitKerjaCustom = data.unit_kerja_custom
  const jabatanCustom = data.jabatan_custom

  return [jabatanCustom, jabatan, unitKerja, unitKerjaCustom].filter(Boolean).join(' ')
}
